import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPalette,
  faTrash,
  faUndo,
  faRedo,
  faTable as faTableIcon,
  faTools,
  faVectorSquare,
} from '@fortawesome/free-solid-svg-icons';
import './Toolbar.css';
import { useI18n } from '../../i18n/I18nContext';

export interface ToolbarProps {
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  readOnly?: boolean;
  mode: 'wall' | 'delete' | null;
  toggleDeleteTool: () => void;
  toggleWallTool: () => void;
  handleAddTable: () => void;
  tableColor: string;
  wallColor: string;
  handleChangeTableColor: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangeWallColor: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const Toolbar: React.FC<ToolbarProps> = ({
  undo,
  redo,
  canUndo,
  canRedo,
  readOnly = false,
  mode,
  toggleDeleteTool,
  toggleWallTool,
  handleAddTable,
  tableColor,
  wallColor,
  handleChangeTableColor,
  handleChangeWallColor,
}) => {
  const { t } = useI18n();

  return (
    <aside className="toolbar">
      <div className="toolbar-panel toolbar-history-panel">
        <div className="toolbar-panel-title">
          <FontAwesomeIcon icon={faUndo} />
          <span>{t('toolbar.history')}</span>
        </div>
        <div className="undo-redo-group">
          <button className="toolbar-history-button" onClick={undo} disabled={!canUndo || readOnly}>
            <FontAwesomeIcon icon={faUndo} />
            <span>{t('table.undo')}</span>
          </button>
          <button className="toolbar-history-button" onClick={redo} disabled={!canRedo || readOnly}>
            <FontAwesomeIcon icon={faRedo} />
            <span>{t('table.redo')}</span>
          </button>
        </div>
      </div>
      <button
        onClick={e => {
          e.stopPropagation();
          toggleDeleteTool();
        }}
        disabled={readOnly}
        className={`toolbar-action toolbar-danger-button ${mode === 'delete' ? 'active delete-active' : ''}`}
      >
        <FontAwesomeIcon icon={faTrash} /> {t('table.delete')}
      </button>
      <span className="separator"></span>
      <div className="toolbar-panel toolbar-tools-panel">
        <div className="toolbar-panel-title">
          <FontAwesomeIcon icon={faTools} />
          <span>{t('toolbar.tools')}</span>
        </div>
        <button
          onClick={e => {
            e.stopPropagation();
            toggleWallTool();
          }}
          disabled={readOnly}
          className={`toolbar-action toolbar-tool-button ${mode === 'wall' ? 'active wall-active' : ''}`}
        >
          <FontAwesomeIcon icon={faVectorSquare} /> {t('table.addWall')}
        </button>
        <button
          onClick={e => {
            e.stopPropagation();
            handleAddTable();
          }}
          disabled={readOnly}
          className="toolbar-action toolbar-tool-button"
        >
          <FontAwesomeIcon icon={faTableIcon} /> {t('table.addTable')}
        </button>
      </div>
      <div className="toolbar-panel colour-pickers">
        <div className="toolbar-panel-title">
          <FontAwesomeIcon icon={faPalette} />
          <span>{t('toolbar.appearance')}</span>
        </div>
        <div className="colour-picker">
          <label>{t('table.tableColor')}</label>
          <input
            type="color"
            value={tableColor}
            disabled={readOnly}
            onChange={handleChangeTableColor}
          />
        </div>
        <div className="colour-picker">
          <label>{t('table.wallColor')}</label>
          <input
            type="color"
            value={wallColor}
            disabled={readOnly}
            onChange={handleChangeWallColor}
          />
        </div>
      </div>
    </aside>
  );
};

export default Toolbar;
